import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../components/ui/use-toast';
import WorkingFileUpload from '../WorkingFileUpload';
import fileUploadService from '../../lib/fileUploadService';
import servicesService from '../../lib/servicesService';

const ServiceImageUploadModal = ({ 
  isOpen, 
  onClose, 
  service, 
  onImageUpdate 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const { toast } = useToast();

  // Initialize current images when service changes
  useEffect(() => {
    if (service) {
      const images = [];
      
      // Add main image if it exists
      if (service.image_url) {
        images.push({
          id: 'main',
          url: service.image_url,
          name: 'Main Service Image',
          isMain: true
        });
      }
      
      setCurrentImages(images);
    }
  }, [service]);

  const handleFileSelect = (file) => {
    console.log('🔍 ServiceImageUploadModal - handleFileSelect called with:', file);
    console.log('🔍 File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    setSelectedFile(file);
    
    // Create preview URL
    console.log('🔍 Creating preview URL...');
    const url = URL.createObjectURL(file);
    console.log('✅ Preview URL created:', url.substring(0, 50) + '...');
    setPreviewUrl(url);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
  };

  const handleUpload = async () => {
    if (!selectedFile || !service) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload and optimize image
      const result = await fileUploadService.uploadAndOptimizeImage(
        selectedFile,
        service.name,
        'service' // Use 'service' as the type for services
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        console.log('🔍 Upload result:', result);
        console.log('🔍 Optimized path:', result.optimizedPath);
        
        // Add new image to current images list
        const newImage = {
          id: Date.now(), // Simple ID for now
          url: result.optimizedPath,
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
          uploadedAt: new Date().toISOString()
        };

        // Update current images state - set new image as main
        setCurrentImages(prev => {
          // Remove old main image and add new one as main
          const filteredImages = prev.filter(img => img.id !== 'main');
          return [
            {
              id: 'main',
              url: newImage.url,
              name: newImage.name,
              isMain: true
            },
            ...filteredImages
          ];
        });

        // Update service with new image as the main image
        const updatedService = {
          ...service,
          image_url: newImage.url
        };

        // Prepare data for database update (exclude id and other fields that shouldn't be updated)
        const updateData = {
          image_url: newImage.url
        };

        console.log('🔍 Updating service with data:', updatedService);
        console.log('🔍 Service ID:', service.id);
        console.log('🔍 Service ID type:', typeof service.id);
        console.log('🔍 Update data for database:', updateData);
        
        try {
          console.log('🔍 Attempting to update service with ID:', service.id);
          console.log('🔍 Service data to update:', updateData);
          const result = await servicesService.updateService(service.id, updateData);
          console.log('✅ Service updated successfully:', result);
        } catch (updateError) {
          console.error('❌ Error updating service:', updateError);
          toast({
            title: "Upload saved, but database update failed",
            description: updateError.message || "Image uploaded but service record was not updated",
            variant: "destructive",
          });
          throw updateError;
        }
        
        toast({
          title: "Success",
          description: "Service image uploaded and optimized successfully!",
        });

        // Reset form
        setSelectedFile(null);
        setPreviewUrl('');
        setUploadProgress(0);

        // Notify parent component
        onImageUpdate(updatedService);

        // Keep modal open to show the new image
        // onClose(); // Removed this line
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl('');
    setUploadProgress(0);
    onClose();
  };

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">
            Manage Images - {service.name}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Images Section */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Current Images</h3>
            {currentImages.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentImages.map((image) => (
                  <Card key={image.id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="relative">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = '/images/placeholder.jpg';
                          }}
                        />
                        {image.isMain && (
                          <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                            Main
                          </div>
                        )}
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-300 truncate">{image.name}</p>
                        <p className="text-xs text-gray-500">
                          {image.size ? `${Math.round(image.size / 1024)} KB` : ''}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No images uploaded yet</p>
              </div>
            )}
          </div>

          {/* Upload New Image Section */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Upload New Image</h3>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <WorkingFileUpload
                  onFileSelect={handleFileSelect}
                  onFileRemove={handleFileRemove}
                  maxSize={10}
                  acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                />

                {previewUrl && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Preview:</h4>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border border-gray-600"
                    />
                  </div>
                )}

                {isUploading && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-gray-300 mb-2">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isUploading ? (
                      <>
                        <Upload className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Image
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceImageUploadModal;
